import { UserOutlined } from "@ant-design/icons";
import { Dropdown, Layout, Typography } from "antd";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ProspoLogo from "../assets/images/icons/Prospo.svg";
import { DataContext } from "../contexts/DataProvider";
import {
  Box,
  CircularProgress,
  Paper,
  Avatar,
  Typography as MuiTypography,
  Tooltip,
  Divider,
  useTheme,
} from "@mui/material";
const { Header, Content } = Layout;
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { fetchPersonalDetails } from "../services/users.service";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ContactsIcon from "@mui/icons-material/Contacts";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import Notifications from "../components/Notifications/Notifications";
import { ReleaseNoteModal } from "../release-notes";
import WikiSearchDialog from "../components/wiki/WikiSearchDialog";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton } from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { hasExperimentalFeature } from "../constants/experimentalFeatures";
import { getLastSectionPath } from "../hooks/useSessionState";

// Sections where "where the user was" lives in the URL itself (Notes' /notes/:id, Files'
// /files?folder=) rather than component state — the sidebar link should return to the last
// visited path under these, not always reset to the section's bare default route.
const SECTIONS_WITH_REMEMBERED_PATH: Record<string, string> = {
  "/notes": "notes",
  "/files": "files",
  "/global-admin": "global-admin",
  "/billing": "financials",
};

interface ContainerProps {
  rootLayoutStyle?: React.CSSProperties;
  parentContentStyle?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  children?: React.ReactNode;
  clientName?: string;
}

export default function Container(props: ContainerProps) {
  const theme = useTheme();
  // User-pinned sidebar preference. Null = follow viewport auto-collapse.
  const initialUserPref = (() => {
    try {
      const stored = localStorage.getItem("prospoSidebarPref");
      return stored === "expanded" || stored === "collapsed"
        ? (stored as "expanded" | "collapsed")
        : null;
    } catch {
      return null;
    }
  })();
  const [userPref, setUserPref] = useState<"expanded" | "collapsed" | null>(
    initialUserPref,
  );
  // Seed from the same effective width handleResize uses (window minus the
  // Prospo Agent side-car). Reading raw innerWidth here disagreed with the
  // resize handler whenever the side-car was already open on mount.
  const initialEffectiveWidth = (() => {
    try {
      const raw = getComputedStyle(document.body).getPropertyValue(
        "--prospo-sidecar-width",
      );
      return window.innerWidth - (raw ? parseInt(raw, 10) || 0 : 0);
    } catch {
      return window.innerWidth;
    }
  })();
  const [collapsed, setCollapsed] = useState(
    initialUserPref
      ? initialUserPref === "collapsed"
      : initialEffectiveWidth < 1200,
  );
  const [isMobile, setIsMobile] = useState(initialEffectiveWidth < 768);

  const navigate = useNavigate();
  const location = useLocation();
  const { logout, login }: any = useContext(DataContext);
  const handleNavigation = (url: string) => () => {
    const section = SECTIONS_WITH_REMEMBERED_PATH[url];
    navigate(section ? getLastSectionPath(section, url) : url);
    // On mobile the sidebar is a full-screen overlay — always close it
    // after any navigation so the destination page isn't blocked.
    if (isMobile) setCollapsed(true);
  };
  const [image, setImage] = useState("");
  const [imageLoaded, setImageLoaded] = useState(false);
  const isHelpDeskActive = location.pathname.startsWith("/helpcenter");
  const isWikiRoute = location.pathname.startsWith("/notes");
  const [wikiSearchOpen, setWikiSearchOpen] = useState(false);

  // Ctrl+K / Cmd+K opens notes search when on notes pages
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "k" && isWikiRoute) {
      e.preventDefault();
      setWikiSearchOpen(true);
    }
  }, [isWikiRoute]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const orgId = login?.data?.orgId;
  // const [trialBannerHeight, setTrialBannerHeight] = useState(0);
  // userId not currently used but preserved if needed in future

  // [NEW] Intelligent Layout Calculation
  const calculateEffectiveWidth = () => {
    // Check if Side-Car is active by reading the CSS variable we set
    const sideCarWidthStr = getComputedStyle(document.body).getPropertyValue(
      "--prospo-sidecar-width",
    );
    const sideCarWidth = sideCarWidthStr ? parseInt(sideCarWidthStr, 10) : 0;

    // The website thinks the screen is this wide:
    return window.innerWidth - sideCarWidth;
  };

  useEffect(() => {
    const handleResize = () => {
      const effectiveWidth = calculateEffectiveWidth();

      // Use effectiveWidth instead of window.innerWidth
      // This forces the app to react EXACTLY as if you opened DevTools
      const mobile = effectiveWidth < 768;

      setIsMobile(mobile);
      // Honor the user's pinned preference if set; otherwise auto-collapse
      // when there isn't enough room.
      if (userPref === null) {
        setCollapsed(effectiveWidth < 1200);
      } else {
        setCollapsed(userPref === "collapsed");
      }
    };

    // Listen to standard resize AND our custom Side-Car updates
    window.addEventListener("resize", handleResize);

    // Run once on mount to check initial state
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [userPref]);

  const fetchImage = async () => {
    try {
      const res: any = await fetchPersonalDetails(orgId);
      const matchingUser = res.find(
        (user: any) => user.userId === login?.data?.id,
      );
      if (matchingUser) {
        setImage(matchingUser.image);
      } else {
        console.warn("No matching user image found.");
      }
    } catch (err) {
      console.error("Error fetching image:", err);
    }
  };

  useEffect(() => {
    if (orgId) fetchImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const { clientName } = props;

  const menu = (
    <Paper
      sx={{
        width: 320,
        borderRadius: "12px",
        boxShadow: "0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
        p: 2,
        border: "1px solid #E5E7EB",
      }}
    >
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Box position="relative" width={44} height={44}>
          {!imageLoaded && (
            <Box
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <Avatar
            src={image || "profile.png"}
            style={{ width: 44, height: 44 }}
            imgProps={{
              onLoad: () => setImageLoaded(true),
              onError: () => setImageLoaded(true),
            }}
          />
        </Box>
        <Box overflow="hidden">
          <MuiTypography variant="subtitle2" fontWeight={600} noWrap>
            {login?.data?.name
              ?.split(" ")
              .map(
                (word: string) => word.charAt(0).toUpperCase() + word.slice(1),
              )
              .join(" ")}
          </MuiTypography>
          <MuiTypography
            variant="caption"
            color="text.secondary"
            display="block"
          >
            {login?.data?.email}
          </MuiTypography>
        </Box>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box
        onClick={() => navigate("/profile")}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: "8px",
          cursor: "pointer",
          "&:hover": { bgcolor: "#F3F4F6" },
        }}
      >
        <SettingsIcon fontSize="small" sx={{ color: "#6B7280" }} />
        <MuiTypography variant="body2" color="text.primary">
          Settings
        </MuiTypography>
      </Box>
      <Box
        onClick={logout}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: "8px",
          cursor: "pointer",
          "&:hover": { bgcolor: "#F3F4F6" },
        }}
      >
        <ExitToAppIcon fontSize="small" sx={{ color: "#6B7280" }} />
        <MuiTypography variant="body2" color="text.primary">
          Logout
        </MuiTypography>
      </Box>
    </Paper>
  );

  const getContentMarginLeft = () => {
    if (isMobile) return 0;
    return collapsed ? 64 : 220;
  };

  return (
    <Layout
      style={{
        background: "#0F172A",
        minHeight: "100vh",
        ...props.rootLayoutStyle,
      }}
    >
      {isMobile && !collapsed && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 999,
          }}
          onClick={() => setCollapsed(true)}
        />
      )}
      {/* ── Redesigned Sidebar ─────────────────────────────── */}
      {(!isMobile || !collapsed) && (
        <Box
          component="nav"
          sx={{
            position: "fixed",
            left: 0,
            top: 64,
            bottom: 0,
            width: collapsed ? 64 : 220,
            backgroundColor: "#0F172A",
            zIndex: 1000,
            transition: "width 0.2s ease",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: "auto",
            borderRight: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          {/* ── Main nav items ─────────────────────────── */}
          <Box sx={{ flex: 1, pt: 1.5, px: 1 }}>
            {[
              {
                key: "/home",
                label: "Home",
                icon: <HomeIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/clients",
                label: "Clients",
                icon: <PeopleOutlinedIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/contacts",
                label: "Contacts",
                icon: <ContactsIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/billing",
                label: "Revenue & Billing",
                icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/documents",
                label: "Documents",
                icon: <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/email",
                label: "Email",
                icon: <EmailOutlinedIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/files",
                label: "Shared Files",
                icon: <FolderSharedOutlinedIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/private-files",
                label: "Private Files",
                icon: <LockOutlinedIcon sx={{ fontSize: 20 }} />,
              },
              {
                key: "/notes",
                label: "Notes",
                icon: <MenuBookOutlinedIcon sx={{ fontSize: 20 }} />,
              },
            ]
              .filter(
                (item) => {
                  if (item.key === "/documents") return hasExperimentalFeature(login?.data, "documents");
                  if (item.key === "/billing")   return hasExperimentalFeature(login?.data, "revenue_billing");
                  return true;
                }
              )
              .map(({ key, label, icon }) => {
              const isActive =
                key === "/email"
                  ? location.pathname.startsWith("/email")
                  : location.pathname === key ||
                    location.pathname.startsWith(key + "/");
              return (
                <Tooltip
                  key={key}
                  title={collapsed ? label : ""}
                  placement="right"
                  arrow
                >
                  <Box
                    onClick={handleNavigation(key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 0.9,
                      borderRadius: "10px",
                      cursor: "pointer",
                      mb: 0.5,
                      minHeight: 40,
                      backgroundColor: isActive
                        ? "rgba(69, 95, 232, 0.15)"
                        : "transparent",
                      color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                      transition:
                        "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(69, 95, 232, 0.15)"
                          : "rgba(69, 95, 232, 0.3)",
                        color: "#FFFFFF",
                        transform: "scale(1.03)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        color: "inherit",
                      }}
                    >
                      {icon}
                    </Box>
                    <MuiTypography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: isActive ? 600 : 500,
                        whiteSpace: "nowrap",
                        color: "inherit",
                        lineHeight: 1,
                        maxWidth: collapsed ? 0 : "160px",
                        opacity: collapsed ? 0 : 1,
                        overflow: "hidden",
                        transition: "max-width 0.2s ease, opacity 0.15s ease",
                      }}
                    >
                      {label}
                    </MuiTypography>
                  </Box>
                </Tooltip>
              );
            })}

            {/* ── Admin-only items ───────────────────── */}
            {(login?.data?.role === "global admin" ||
              login?.data?.role === "orgadmin") && (
              <Box
                sx={{
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <MuiTypography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.3)",
                    px: 1.5,
                    pb: 0.5,
                    mb: 0.5,
                    maxWidth: collapsed ? 0 : "160px",
                    opacity: collapsed ? 0 : 1,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    transition: "max-width 0.2s ease, opacity 0.15s ease",
                  }}
                >
                  Admin
                </MuiTypography>
                {(() => {
                  const isActive =
                    location.pathname === "/admin" ||
                    location.pathname === "/organisations";
                  return (
                    <Tooltip
                      title={collapsed ? "Admin" : ""}
                      placement="right"
                      arrow
                    >
                      <Box
                        onClick={handleNavigation("/admin")}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 1.5,
                          py: 0.9,
                          borderRadius: "10px",
                          cursor: "pointer",
                          mb: 0.5,
                          minHeight: 40,
                          backgroundColor: isActive
                            ? "rgba(69, 95, 232, 0.15)"
                            : "transparent",
                          color: isActive
                            ? "#FFFFFF"
                            : "rgba(255,255,255,0.55)",
                          transition:
                            "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
                          "&:hover": {
                            backgroundColor: isActive
                              ? "rgba(69, 95, 232, 0.15)"
                              : "rgba(69, 95, 232, 0.3)",
                            color: "#FFFFFF",
                            transform: "scale(1.03)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexShrink: 0,
                            color: "inherit",
                          }}
                        >
                          <AdminPanelSettingsOutlinedIcon
                            sx={{ fontSize: 20 }}
                          />
                        </Box>
                        <MuiTypography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: isActive ? 600 : 500,
                            whiteSpace: "nowrap",
                            color: "inherit",
                            lineHeight: 1,
                            maxWidth: collapsed ? 0 : "160px",
                            opacity: collapsed ? 0 : 1,
                            overflow: "hidden",
                            transition:
                              "max-width 0.2s ease, opacity 0.15s ease",
                          }}
                        >
                          Admin
                        </MuiTypography>
                      </Box>
                    </Tooltip>
                  );
                })()}
                {login?.data?.role === "global admin" &&
                  (() => {
                    const isActive = location.pathname === "/global-admin";
                    return (
                      <Tooltip
                        title={collapsed ? "Global Admin" : ""}
                        placement="right"
                        arrow
                      >
                        <Box
                          onClick={handleNavigation("/global-admin")}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 1.5,
                            py: 0.9,
                            borderRadius: "10px",
                            cursor: "pointer",
                            mb: 0.5,
                            minHeight: 40,
                            backgroundColor: isActive
                              ? "rgba(69, 95, 232, 0.15)"
                              : "transparent",
                            color: isActive
                              ? "#FFFFFF"
                              : "rgba(255,255,255,0.55)",
                            transition:
                              "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
                            "&:hover": {
                              backgroundColor: isActive
                                ? "rgba(69, 95, 232, 0.15)"
                                : "rgba(69, 95, 232, 0.3)",
                              color: "#FFFFFF",
                              transform: "scale(1.03)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexShrink: 0,
                              color: "inherit",
                            }}
                          >
                            <ShieldOutlinedIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <MuiTypography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: isActive ? 600 : 500,
                              whiteSpace: "nowrap",
                              color: "inherit",
                              lineHeight: 1,
                              maxWidth: collapsed ? 0 : "160px",
                              opacity: collapsed ? 0 : 1,
                              overflow: "hidden",
                              transition:
                                "max-width 0.2s ease, opacity 0.15s ease",
                            }}
                          >
                            Global Admin
                          </MuiTypography>
                        </Box>
                      </Tooltip>
                    );
                  })()}
              </Box>
            )}
          </Box>

          {/* ── Bottom: Settings ────────────────────────── */}
          <Box
            sx={{
              px: 1,
              pb: 0.5,
              pt: 1,
              borderTop: "1px solid rgba(255,255,255,0.06)",
            }}
          >
            {(() => {
              const isActive = location.pathname === "/profile";
              return (
                <Tooltip
                  title={collapsed ? "Settings" : ""}
                  placement="right"
                  arrow
                >
                  <Box
                    onClick={() => {
                      navigate("/profile");
                      if (isMobile) setCollapsed(true);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 0.9,
                      borderRadius: "10px",
                      cursor: "pointer",
                      minHeight: 40,
                      backgroundColor: isActive
                        ? "rgba(69, 95, 232, 0.15)"
                        : "transparent",
                      color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                      transition:
                        "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(69, 95, 232, 0.15)"
                          : "rgba(69, 95, 232, 0.3)",
                        color: "#FFFFFF",
                        transform: "scale(1.03)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        color: "inherit",
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <MuiTypography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: isActive ? 600 : 500,
                        whiteSpace: "nowrap",
                        color: "inherit",
                        lineHeight: 1,
                        maxWidth: collapsed ? 0 : "160px",
                        opacity: collapsed ? 0 : 1,
                        overflow: "hidden",
                        transition: "max-width 0.2s ease, opacity 0.15s ease",
                      }}
                    >
                      Settings
                    </MuiTypography>
                  </Box>
                </Tooltip>
              );
            })()}
          </Box>

          {/* ── Bottom: Help ───────────────────────────── */}
          <Box
            sx={{
              px: 1,
              pb: 1.5,
              borderTop: "1px solid rgba(255,255,255,0.06)",
              pt: 1,
            }}
          >
            {(() => {
              const isActive = isHelpDeskActive;
              return (
                <Tooltip
                  title={collapsed ? "Help Center" : ""}
                  placement="right"
                  arrow
                >
                  <Box
                    onClick={() => {
                      navigate(getLastSectionPath("helpcenter", "/helpcenter"));
                      if (isMobile) setCollapsed(true);
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 0.9,
                      borderRadius: "10px",
                      cursor: "pointer",
                      minHeight: 40,
                      backgroundColor: isActive
                        ? "rgba(69, 95, 232, 0.15)"
                        : "transparent",
                      color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)",
                      transition:
                        "background-color 0.15s ease, color 0.15s ease, transform 0.15s ease",
                      "&:hover": {
                        backgroundColor: isActive
                          ? "rgba(69, 95, 232, 0.15)"
                          : "rgba(69, 95, 232, 0.3)",
                        color: "#FFFFFF",
                        transform: "scale(1.03)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        color: "inherit",
                      }}
                    >
                      <HelpOutlineIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <MuiTypography
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: isActive ? 600 : 500,
                        whiteSpace: "nowrap",
                        color: "inherit",
                        lineHeight: 1,
                        maxWidth: collapsed ? 0 : "160px",
                        opacity: collapsed ? 0 : 1,
                        overflow: "hidden",
                        transition: "max-width 0.2s ease, opacity 0.15s ease",
                      }}
                    >
                      Help Center
                    </MuiTypography>
                  </Box>
                </Tooltip>
              );
            })()}
          </Box>
        </Box>
      )}

      {/* ── Sidebar collapse/expand edge rail ──────────────────────────────
          Shown whenever the sidebar itself is on screen. Previously hidden for
          the whole mobile breakpoint, which left a dead zone: with the Prospo
          Agent side-car open, the effective width drops below 768 while the
          window is still wide, so the layout stayed desktop-shaped but lost its
          only collapse control and the expanded sidebar covered the page.
          On mobile the sidebar is an overlay that only exists while expanded,
          so the rail is only useful (and only rendered) in that state. */}
      {(!isMobile || !collapsed) && (
        <Tooltip
          title={
            isMobile
              ? "Close sidebar"
              : collapsed
                ? "Expand sidebar"
                : "Collapse sidebar"
          }
          placement="right"
        >
          <Box
            onClick={() => {
              // On mobile the sidebar is a transient overlay — dismissing it is
              // not a pinned preference, so don't persist one. Otherwise the
              // desktop sidebar would silently start collapsed later.
              if (isMobile) {
                setCollapsed(true);
                return;
              }
              const next: "expanded" | "collapsed" = collapsed
                ? "expanded"
                : "collapsed";
              try {
                localStorage.setItem("prospoSidebarPref", next);
              } catch {
                /* ignore storage errors */
              }
              setUserPref(next);
              setCollapsed(next === "collapsed");
            }}
            sx={{
              position: "fixed",
              top: 64,
              bottom: 0,
              // The mobile overlay is 220px wide and always expanded, so the
              // rail sits at its edge rather than the collapsed-rail offset.
              left: isMobile ? 214 : collapsed ? 58 : 214,
              width: 16,
              zIndex: 1100,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "left 0.2s ease",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                bottom: 0,
                left: "50%",
                width: "2px",
                transform: "translateX(-50%)",
                backgroundColor: "transparent",
                transition: "background-color 0.2s ease",
              },
              "&:hover::before": {
                backgroundColor: "rgba(25, 118, 210, 0.45)",
              },
              "&:hover .rail-handle": {
                opacity: 1,
                transform: "translateX(0) scale(1.08)",
                boxShadow: "0 3px 14px rgba(0,0,0,0.28)",
              },
            }}
          >
            <Box
              className="rail-handle"
              sx={{
                width: 28,
                height: 60,
                borderRadius: "10px",
                backgroundColor: "#1E293B",
                border: "1.5px solid rgba(255,255,255,0.18)",
                color: "rgba(255,255,255,0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.25,
                transform: "translateX(0)",
                transition:
                  "opacity 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
                boxShadow: "0 2px 8px rgba(0,0,0,0.22)",
                pointerEvents: "none",
              }}
            >
              <ChevronLeftIcon
                sx={{
                  fontSize: 16,
                  transform: collapsed ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
            </Box>
          </Box>
        </Tooltip>
      )}

      <Header
        style={{
          position: "fixed",
          top: 0,
          zIndex: 1000,
          padding: 0,
          left: 0,
          right: "var(--prospo-sidecar-width, 0px)",
          width: "auto",
          transition: "right 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        className="site-layout-background"
      >
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "space-between" : "space-between",
            alignItems: "center",
            padding: "0px 20px",
            height: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            {isMobile && (
              <MenuIcon
                style={{ color: "#fff", cursor: "pointer", fontSize: "24px" }}
                onClick={() => setCollapsed(!collapsed)}
              />
            )}
            <img
              style={{
                height: 32,
                cursor: "pointer",
                position: isMobile ? "absolute" : "static",
                left: isMobile ? "50%" : "auto",
                transform: isMobile ? "translateX(-50%)" : "none",
              }}
              onClick={() => {
                navigate("/home");
                if (isMobile) setCollapsed(true);
              }}
              src={ProspoLogo}
              alt="Logo"
            />
          </div>
          {clientName && !isMobile ? (
            <div style={{ flexGrow: 1, textAlign: "center" }}>
              <Typography.Title level={4} style={{ color: "#fff", margin: 0 }}>
                {clientName}
              </Typography.Title>
            </div>
          ) : null}
          <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
            {isWikiRoute && (
              <Tooltip title="Search notes (Ctrl+K)" placement="bottom">
                <IconButton size="small" sx={{ color: "#fff", p: 0.5 }} onClick={() => setWikiSearchOpen(true)}>
                  <SearchIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
            <Notifications />
            {isWikiRoute && (
              <WikiSearchDialog open={wikiSearchOpen} onClose={() => setWikiSearchOpen(false)} />
            )}
            <Dropdown
              overlay={menu}
              trigger={["click"]}
              overlayStyle={{ color: "#fff" }}
              onOpenChange={(open) => {
                if (open) fetchImage();
              }}
            >
              <a
                className="ant-dropdown-link"
                style={{
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                }}
                onClick={(e) => e.preventDefault()}
              >
                {!isMobile && login?.data?.email}
                {isMobile && <UserOutlined style={{ fontSize: "20px" }} />}
              </a>
            </Dropdown>
          </div>
        </div>
      </Header>

      <Content
        style={{
          padding: isMobile ? `26px 12px 24px 12px` : `26px 24px 24px 24px`,
          background: theme.palette.background.default,
          marginLeft: getContentMarginLeft(),
          marginRight: "var(--prospo-sidecar-width, 0px)",
          transition:
            "margin 0.2s cubic-bezier(0.4, 0, 0.2, 1), padding 0.2s, border-radius 0.2s",
          marginTop: "68px",
          borderTopLeftRadius: "16px",
          borderBottomLeftRadius: "16px",
          ...props.parentContentStyle,
        }}
      >
        <div style={{ minHeight: 360, ...props.contentStyle }}>
          {props.children}
        </div>
      </Content>

      {/* Shows the newest undismissed release note once per user, on next login. */}
      <ReleaseNoteModal enabled={Boolean(login?.data?.id)} />
    </Layout>
  );
}
